import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { FormSelect, FormInput, FormCheckbox } from '@/components/forms/FormField';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import { Tabs, TabPanel } from '@/components/ui/Tabs';
import {
  useCreateJob,
  useCreateGroupJobs,
  useUpdateJob,
  useJobDetail,
  useWorkflowTemplates,
  type CreateJobRequest,
  type UpdateJobRequest,
  type GroupJobsResult,
} from './hooks/useJobs';
import { GroupMemberJobFanout } from './GroupMemberJobFanout';

/**
 * Pull a human-readable message out of an API/Axios error so the toast shows
 * what actually went wrong (e.g. a Zod validation message) instead of a
 * generic failure string.
 */
function extractApiErrorMessage(err: unknown, fallback: string): string {
  const apiError = (
    err as {
      response?: {
        data?: {
          error?: {
            message?: string;
            details?: unknown;
          };
        };
      };
    }
  )?.response?.data?.error;

  if (!apiError) return fallback;

  // Zod validation errors carry a `details` array of { path, message }.
  if (Array.isArray(apiError.details)) {
    const fieldMessages = apiError.details
      .map((d) => (d as { message?: string })?.message)
      .filter((m): m is string => Boolean(m));
    if (fieldMessages.length > 0) return fieldMessages.join(', ');
  }

  return apiError.message || fallback;
}

/** Human-readable summary of a group fan-out result for a toast. */
function summarizeGroupResult(result: GroupJobsResult): string {
  const parts: string[] = [];
  if (result.successful > 0) parts.push(`created ${result.successful} member job(s)`);
  if (result.skipped > 0) parts.push(`skipped ${result.skipped} (unfinished job already exists)`);
  if (result.failed > 0) parts.push(`${result.failed} failed`);
  return parts.length > 0 ? `Group members: ${parts.join(', ')}.` : '';
}
import { useJobEntityTypes } from './hooks/useJobEntityTypes';
import { useResolveFormForEntityType } from '@/features/form-builder/hooks/useFormBuilder';
import { useSystemLookups } from '@/features/settings/hooks/useSystemLookups';
import {
  DynamicFormRenderer,
  validateFieldPattern,
} from '@/features/form-builder/DynamicFormRenderer';
import { useToast } from '@/components/ui/Toast';
import { useQuery } from '@tanstack/react-query';
import { apiClient, type ApiResponse } from '@/lib/api-client';
import { useContact } from '@/features/contacts/hooks/useContacts';
import {
  extractFinancialStart,
  suggestRecurrenceNextDate,
  todayYmd,
  tomorrowYmd,
  type JobRecurrenceFrequency,
} from './recurrenceSuggestions';

const RECURRENCE_FIELD_KEYS = new Set([
  'is_recurring',
  'recurrence_frequency',
  'recurrence_next_date',
  'recurrence_advance_days',
]);

const FREQUENCY_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
];

function toDateInput(value: unknown): string {
  if (!value) return '';
  return String(value).slice(0, 10);
}

/**
 * JobForm — create or edit a job.
 * Uses the dynamic form renderer based on the selected job entity type's form definition.
 * Mirrors the ContactForm pattern for dynamic rendering.
 */
export function JobForm() {
  const { jobId } = useParams<{ jobId: string }>();
  const [searchParams] = useSearchParams();
  const prefilledClientId = searchParams.get('clientId') ?? '';
  const isEditMode = Boolean(jobId);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { can } = usePermissions();
  const canChangeWorkflow = can(PERMISSIONS.JOBS_CHANGE_WORKFLOW);
  const canAssign = can(PERMISSIONS.JOBS_ASSIGN);

  // If user lacks assign permission, disable partner/manager/accountant dropdowns
  const disabledCategories = canAssign
    ? []
    : ['users_partner', 'users_manager', 'users_accountant'];
  const createJob = useCreateJob();
  const createGroupJobs = useCreateGroupJobs();
  const updateJob = useUpdateJob();
  const { data: existingJob, isLoading: jobLoading } = useJobDetail(jobId ?? '');
  const { data: jobEntityTypes } = useJobEntityTypes();
  const { data: workflowTemplates } = useWorkflowTemplates();
  // 'job_types' system lookups bridge the job form (which uses the entity-type
  // NAME) and workflow templates (which store the lookup CODE in job_type).
  const { data: jobTypeLookups } = useSystemLookups('job_types');

  const [selectedEntityTypeId, setSelectedEntityTypeId] = useState('');
  // Group members (of the selected client's groups) chosen to ALSO get this job.
  const [selectedGroupMemberIds, setSelectedGroupMemberIds] = useState<string[]>([]);
  // Of the selected members, those who already have an OPEN job of this type.
  const [duplicateGroupMemberIds, setDuplicateGroupMemberIds] = useState<string[]>([]);
  const { data: formDefinition, isLoading: formLoading } =
    useResolveFormForEntityType(selectedEntityTypeId);

  const methods = useForm({
    // Validate on blur so field formats (regex) surface inline, not only on save.
    mode: 'onBlur',
    defaultValues: {
      entityTypeId: '',
      title: '',
      description: '',
      clientId: prefilledClientId,
      practiceEntityId: '',
      jobType: '',
      workflowTemplateId: '',
      priority: 'medium',
      assignedPartnerId: '',
      assignedManagerId: '',
      assignedAccountantId: '',
      budgetAmount: '',
      estimatedHours: '',
      startDate: '',
      dueDate: '',
      isRecurring: false,
      recurrenceFrequency: '',
      recurrenceNextDate: '',
      customFields: {} as Record<string, unknown>,
    } as Record<string, unknown>,
  });

  const { handleSubmit, reset, watch } = methods;

  // Sync entity type selection with form state
  const watchedEntityTypeId = watch('entityTypeId') as string;
  useEffect(() => {
    if (watchedEntityTypeId && watchedEntityTypeId !== selectedEntityTypeId) {
      setSelectedEntityTypeId(watchedEntityTypeId);
    }
  }, [watchedEntityTypeId, selectedEntityTypeId]);

  // Auto-populate the job's assigned partner from the selected client. A
  // contact's "partner" (contacts.assigned_partner_id) is a staff user — the
  // same type as jobs.assigned_partner_id — so it copies across directly.
  const watchedClientId = watch('clientId') as string;
  const { data: selectedClient } = useContact(watchedClientId || '');
  const lastPartnerClientId = useRef<string | undefined>(undefined);
  useEffect(() => {
    // Never override an existing job's partner while editing.
    if (isEditMode) return;
    // Wait until the chosen client's details have loaded.
    if (!watchedClientId || !selectedClient || selectedClient.id !== watchedClientId) return;
    // Only react when the client selection actually changed, so we don't
    // clobber a partner the user manually set/cleared for the same client.
    if (lastPartnerClientId.current === watchedClientId) return;
    lastPartnerClientId.current = watchedClientId;

    const partnerId = selectedClient.assignedPartnerId;
    if (partnerId) {
      methods.setValue('assignedPartnerId' as never, partnerId as never, { shouldDirty: true });
    }
  }, [watchedClientId, selectedClient, isEditMode, methods]);

  // Notify user when no form is attached to selected job type
  useEffect(() => {
    if (selectedEntityTypeId && !formLoading && !formDefinition) {
      toast({
        variant: 'warning',
        title: 'No form attached to this job type',
        description: 'Please configure a form in Settings → Form Builder.',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntityTypeId, formLoading, formDefinition]);

  // Apply default values from form definition when creating a new record
  useEffect(() => {
    if (isEditMode || !formDefinition?.sections) return;

    const camelCaseFn = (str: string) =>
      str.replace(/_([a-z])/g, (_: string, c: string) => c.toUpperCase());

    for (const section of formDefinition.sections) {
      for (const field of section.fields) {
        if (!field.is_active || !field.default_value) continue;

        const fieldName =
          field.is_system_field && field.db_column
            ? camelCaseFn(field.db_column)
            : `customFields.${field.field_key}`;

        // Only set if the current value is empty (don't overwrite user input)
        const currentValue = methods.getValues(fieldName as never) as unknown;
        if (currentValue === '' || currentValue === undefined || currentValue === null) {
          methods.setValue(fieldName as never, field.default_value as never);
        }
      }
    }
  }, [formDefinition, isEditMode, methods]);

  // ─── Workflow template resolution for the selected JOB TYPE ────────────────
  //
  // Mapping note: the job form's job type is the entity-type NAME (which equals
  // the `job_types` system-lookup LABEL), while workflow templates store that
  // lookup's CODE in `job_type`. We bridge name → code via the `job_types`
  // lookups: find the lookup whose label matches the selected job type, take
  // its code, and match templates on that code. A direct name/code equality is
  // kept as a fallback for tenants where code == label.
  const watchedJobType = watch('jobType') as string;

  const resolvedJobType =
    watchedJobType ||
    (jobEntityTypes ?? []).find((et) => et.id === selectedEntityTypeId)?.name ||
    '';

  // Active workflow templates whose job_type matches the selected job type.
  const matchingWorkflowTemplates = useMemo(() => {
    if (!resolvedJobType || !workflowTemplates) return [];
    const norm = (s: string) => s.trim().toLowerCase();
    const target = norm(resolvedJobType);
    const lookupCode = (jobTypeLookups ?? []).find((l) => norm(l.label) === target)?.code;
    return workflowTemplates.filter((t) => {
      if (t.isActive === false) return false;
      const tJobType = norm(t.jobType || '');
      return (!!lookupCode && tJobType === norm(lookupCode)) || tJobType === target;
    });
  }, [resolvedJobType, workflowTemplates, jobTypeLookups]);

  // Options for the header workflow selector.
  const workflowTemplateOptions = useMemo(
    () => matchingWorkflowTemplates.map((t) => ({ value: t.id, label: t.name || t.id })),
    [matchingWorkflowTemplates],
  );

  // Does the form-builder template already position a workflow-template field?
  // If so, we do NOT render our own header selector (avoid duplication); the
  // form's field is authoritative. If not, we render one in the header.
  const formHasWorkflowField = useMemo(() => {
    const sections = formDefinition?.sections ?? [];
    return sections.some((s) =>
      s.fields?.some(
        (f) =>
          f.is_active !== false &&
          (f.db_column === 'workflow_template_id' || f.field_key === 'workflow_template_id'),
      ),
    );
  }, [formDefinition]);

  // Auto-select the first matching workflow template onto `workflowTemplateId`.
  //
  // This sets the value whether or not the field is visible: the header
  // selector (or the form-builder field) shows the pre-selected one, and the
  // value is sent at submit so the backend instantiates the template's
  // stages/tasks. If no template matches, nothing is set. Never overrides a
  // non-empty value, so a user's manual pick (or a form default_value) is
  // preserved; never runs in edit mode so an existing job's workflow is
  // untouched.
  useEffect(() => {
    if (isEditMode) return;
    if (matchingWorkflowTemplates.length === 0) return;

    const current = (methods.getValues('workflowTemplateId' as never) as unknown as string) || '';
    if (current) return;

    methods.setValue('workflowTemplateId' as never, matchingWorkflowTemplates[0].id as never, {
      shouldDirty: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchingWorkflowTemplates, isEditMode]);

  // Populate form when editing
  useEffect(() => {
    if (existingJob && isEditMode) {
      // Resolve entity type ID: use stored value or match by job_type name
      let resolvedEntityTypeId = existingJob.entityTypeId ?? '';
      if (!resolvedEntityTypeId && existingJob.jobType && jobEntityTypes) {
        const match = jobEntityTypes.find(
          (et) => et.name.toLowerCase() === existingJob.jobType.toLowerCase(),
        );
        if (match) resolvedEntityTypeId = match.id;
      }
      // If still no match, use the first available entity type as fallback
      if (!resolvedEntityTypeId && jobEntityTypes && jobEntityTypes.length > 0) {
        resolvedEntityTypeId = jobEntityTypes[0].id;
      }

      const values: Record<string, unknown> = {
        entityTypeId: resolvedEntityTypeId,
        customFields: {},
      };

      // Load ALL field values from form_data (the single source of truth)
      const jobData = existingJob as unknown as Record<string, unknown>;
      const formDataRaw = jobData.formData || jobData.form_data;
      const savedFormData =
        typeof formDataRaw === 'string'
          ? JSON.parse(formDataRaw)
          : ((formDataRaw as Record<string, unknown> | undefined) ?? {});

      const camelCaseFn = (str: string) =>
        str.replace(/_([a-z])/g, (_: string, c: string) => c.toUpperCase());

      // Coerce a stored value into the shape a given field type expects, so
      // every field type hydrates reliably in edit mode.
      const coerceByType = (fieldType: string, raw: unknown): unknown => {
        if (fieldType === 'checkbox') {
          if (typeof raw === 'boolean') return raw;
          if (typeof raw === 'string') return raw === 'true' || raw === '1';
          return !!raw;
        }
        if (fieldType === 'multi_select') {
          if (Array.isArray(raw)) return raw;
          if (typeof raw === 'string' && raw.trim()) {
            try {
              const parsed = JSON.parse(raw);
              return Array.isArray(parsed) ? parsed : [raw];
            } catch {
              return raw.includes(',') ? raw.split(',').map((s) => s.trim()) : [raw];
            }
          }
          return [];
        }
        if (fieldType === 'table') {
          if (Array.isArray(raw)) return raw;
          if (typeof raw === 'string' && raw.trim()) {
            try {
              const parsed = JSON.parse(raw);
              return Array.isArray(parsed) ? parsed : [];
            } catch {
              return [];
            }
          }
          return [];
        }
        return raw ?? '';
      };

      // Populate form from form_data + job object using form definition
      if (formDefinition?.sections) {
        for (const section of formDefinition.sections) {
          for (const field of section.fields) {
            if (!field.is_active) continue;
            if (field.is_system_field && field.db_column) {
              const camelKey = camelCaseFn(field.db_column);
              const val =
                savedFormData[field.db_column] ??
                savedFormData[field.field_key] ??
                jobData[camelKey];
              values[camelKey] = coerceByType(field.field_type, val);
            } else {
              // Custom field
              const val = savedFormData[field.field_key];
              (values.customFields as Record<string, unknown>)[field.field_key] = coerceByType(
                field.field_type,
                val,
              );
            }
          }
        }
      }

      // Fallback: ensure basic fields always populated
      if (!values.title) values.title = existingJob.title ?? '';
      if (!values.clientId) values.clientId = existingJob.clientId ?? '';
      if (!values.priority) values.priority = existingJob.priority ?? 'medium';
      values.isRecurring = existingJob.isRecurring ?? false;
      values.recurrenceFrequency = existingJob.recurrenceFrequency ?? '';
      values.recurrenceNextDate = toDateInput(existingJob.recurrenceNextDate);

      reset(values);
      setSelectedEntityTypeId(resolvedEntityTypeId);
    }
  }, [existingJob, isEditMode, reset, jobEntityTypes, formDefinition]);

  /**
   * Decides whether to proceed with the group fan-out and whether duplicates
   * are allowed. If any selected member already has an open job of this type,
   * prompt the user: confirm → create anyway (allow_duplicates true); cancel →
   * skip those members (allow_duplicates false). Returns null to abort entirely.
   */
  const resolveAllowDuplicates = (): boolean | null => {
    if (duplicateGroupMemberIds.length === 0) return false;
    const proceed = window.confirm(
      `${duplicateGroupMemberIds.length} selected member(s) already have an unfinished job of this type.\n\n` +
        `Click OK to create the job for them anyway, or Cancel to skip those members ` +
        `(the job will still be created for the others).`,
    );
    // OK → allow duplicates; Cancel → don't allow (server skips them). Never a
    // full abort here so the non-duplicate members still get their jobs.
    return proceed;
  };

  const onSubmit = async (values: Record<string, unknown>) => {
    // Validate required fields from form definition
    if (formDefinition?.sections) {
      const missingFields: string[] = [];
      const formatErrors: string[] = [];
      for (const section of formDefinition.sections) {
        for (const field of section.fields) {
          if (!field.is_active) continue;
          if (
            RECURRENCE_FIELD_KEYS.has(field.field_key) ||
            RECURRENCE_FIELD_KEYS.has(field.db_column ?? '')
          ) {
            continue;
          }

          let fieldValue: unknown;
          if (field.is_system_field && field.db_column) {
            const camelKey = field.db_column.replace(/_([a-z])/g, (_: string, c: string) =>
              c.toUpperCase(),
            );
            // Use getValues with specific path for reliable retrieval
            fieldValue = methods.getValues(camelKey as never);
          } else {
            // Custom fields are registered as 'customFields.field_key'
            fieldValue = methods.getValues(`customFields.${field.field_key}` as never);
            // Fallback: check customFields object directly
            if (fieldValue === undefined) {
              const allCustom = methods.getValues('customFields' as never) as
                | Record<string, unknown>
                | undefined;
              if (allCustom) {
                fieldValue = allCustom[field.field_key];
              }
            }
          }

          // Check if the field itself is required and empty
          if (field.is_required) {
            if (
              fieldValue === undefined ||
              fieldValue === null ||
              fieldValue === '' ||
              (Array.isArray(fieldValue) && fieldValue.length === 0)
            ) {
              missingFields.push(field.label);
              continue; // No point checking columns if table is empty
            }
          }

          // Regex format enforcement (validation_rules.pattern) for text-like
          // fields. Empty values are allowed here (required handled above).
          const patternError = validateFieldPattern(field, fieldValue);
          if (patternError) {
            formatErrors.push(patternError);
            continue;
          }

          // Table field column validation — only validates columns visible on the form
          if (field.field_type === 'table') {
            // Parse validation_rules robustly (might be string from API in some edge cases)
            let vRules: Record<string, unknown> = {};
            if (typeof field.validation_rules === 'string') {
              try {
                vRules = JSON.parse(field.validation_rules);
              } catch {
                /* ignore */
              }
            } else if (field.validation_rules && typeof field.validation_rules === 'object') {
              vRules = field.validation_rules as Record<string, unknown>;
            }

            // Only validate columns that are actually rendered (present in field.options)
            const visibleColumnKeys = new Set((field.options || []).map((o) => o.value));

            const colRequired = vRules.column_required as Record<string, boolean> | undefined;
            const colUnique = vRules.column_unique as Record<string, boolean> | undefined;
            const hasRequiredColumns =
              colRequired &&
              Object.entries(colRequired).some(([key, val]) => val && visibleColumnKeys.has(key));

            // If any visible column is required but table has no rows, require at least one row
            if (hasRequiredColumns && (!Array.isArray(fieldValue) || fieldValue.length === 0)) {
              missingFields.push(`${field.label} (at least one row required)`);
            }

            if (Array.isArray(fieldValue) && fieldValue.length > 0) {
              if (colRequired) {
                for (const [colKey, isReq] of Object.entries(colRequired)) {
                  if (!isReq) continue;
                  // Skip columns that are not visible on the form
                  if (!visibleColumnKeys.has(colKey)) continue;
                  const emptyRows = (fieldValue as Record<string, string>[]).filter(
                    (row) => !row[colKey],
                  );
                  if (emptyRows.length > 0) {
                    const colLabel =
                      field.options?.find((o) => o.value === colKey)?.label || colKey;
                    missingFields.push(
                      `${field.label} → ${colLabel} (row has empty required value)`,
                    );
                    break;
                  }
                }
              }

              if (colUnique) {
                for (const [colKey, isUniq] of Object.entries(colUnique)) {
                  if (!isUniq) continue;
                  // Skip columns that are not visible on the form
                  if (!visibleColumnKeys.has(colKey)) continue;
                  const values = (fieldValue as Record<string, string>[])
                    .map((row) => row[colKey])
                    .filter(Boolean);
                  const hasDuplicates = values.length !== new Set(values).size;
                  if (hasDuplicates) {
                    const colLabel =
                      field.options?.find((o) => o.value === colKey)?.label || colKey;
                    missingFields.push(`${field.label} → ${colLabel} (has duplicate values)`);
                    break;
                  }
                }
              }
            }
          }
        }
      }

      if (missingFields.length > 0) {
        toast({
          variant: 'error',
          title: 'Required fields missing',
          description: `Please fill in: ${missingFields.join(', ')}`,
        });
        return;
      }

      if (formatErrors.length > 0) {
        toast({
          variant: 'error',
          title: 'Invalid format',
          description: formatErrors.join(', '),
        });
        return;
      }
    }

    const isRecurring = Boolean(methods.getValues('isRecurring' as never));
    if (isRecurring) {
      const frequency = methods.getValues('recurrenceFrequency' as never);
      const nextDate = String(methods.getValues('recurrenceNextDate' as never) ?? '').slice(0, 10);
      const missingRecurrence: string[] = [];
      if (!frequency) missingRecurrence.push('Frequency');
      if (!nextDate) missingRecurrence.push('Next Date');
      if (nextDate && nextDate < tomorrowYmd()) {
        missingRecurrence.push('Next Date (must be tomorrow or later)');
      }
      if (missingRecurrence.length > 0) {
        toast({
          variant: 'error',
          title: 'Required fields missing',
          description: `Please fill in: ${missingRecurrence.join(', ')}`,
        });
        return;
      }
    }

    const { customFields, entityTypeId, ...systemFields } = values;

    // Build form_data JSON from ALL form values (single source of truth)
    const formData: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(systemFields)) {
      if (value !== undefined && value !== '') {
        const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
        formData[snakeKey] = value;
      }
    }
    // Add custom fields
    if (customFields && typeof customFields === 'object') {
      for (const [key, val] of Object.entries(customFields as Record<string, unknown>)) {
        if (val !== undefined && val !== '') formData[key] = val;
      }
    }

    // Resolve jobType
    const selectedEntityType = (jobEntityTypes ?? []).find(
      (et) => et.id === (entityTypeId as string),
    );
    const resolvedJobType = (systemFields.jobType as string) || selectedEntityType?.name || '';
    if (resolvedJobType) formData.job_type = resolvedJobType;

    if (isEditMode && jobId) {
      // Check if workflow template changed — confirm before proceeding
      const newTemplateId = (systemFields.workflowTemplateId as string) || null;
      const oldTemplateId = existingJob?.workflowTemplateId || null;
      if (newTemplateId !== oldTemplateId && oldTemplateId) {
        if (!canChangeWorkflow) {
          toast({
            variant: 'error',
            title: 'You do not have permission to change the workflow template',
          });
          return;
        }
        const confirmed = window.confirm(
          'You changed the workflow template. This will delete all existing stages and tasks and replace them with the new template. Continue?',
        );
        if (!confirmed) return;
      }

      const request: UpdateJobRequest = {
        clientId: (systemFields.clientId as string) || undefined,
        title: (systemFields.title as string) || undefined,
        description: (systemFields.description as string) || undefined,
        priority: (systemFields.priority as UpdateJobRequest['priority']) || undefined,
        workflowTemplateId: (systemFields.workflowTemplateId as string) || null,
        assignedPartnerId: (systemFields.assignedPartnerId as string) || null,
        assignedManagerId: (systemFields.assignedManagerId as string) || null,
        assignedAccountantId: (systemFields.assignedAccountantId as string) || null,
        budgetAmount: (systemFields.budgetAmount as string)
          ? parseFloat(systemFields.budgetAmount as string)
          : null,
        estimatedHours: (systemFields.estimatedHours as string)
          ? parseFloat(systemFields.estimatedHours as string)
          : null,
        startDate: (systemFields.startDate as string) || null,
        dueDate: (systemFields.dueDate as string) || null,
        isRecurring,
        recurrenceFrequency: isRecurring
          ? (systemFields.recurrenceFrequency as string) || null
          : null,
        recurrenceNextDate: isRecurring
          ? (systemFields.recurrenceNextDate as string) || null
          : null,
        formData,
      };

      try {
        await updateJob.mutateAsync({ id: jobId, ...request });

        // Fan the same job out to any selected group members (each gets a new
        // individual job). This does not modify the current job.
        if (selectedGroupMemberIds.length > 0) {
          const allowDuplicates = resolveAllowDuplicates();
          try {
            const result = await createGroupJobs.mutateAsync({
              clientIds: selectedGroupMemberIds,
              allowDuplicates: allowDuplicates === true,
              entityTypeId: (entityTypeId as string) || undefined,
              practiceEntityId: (systemFields.practiceEntityId as string) || undefined,
              jobType: resolvedJobType || undefined,
              workflowTemplateId: (systemFields.workflowTemplateId as string) || undefined,
              title: (systemFields.title as string) || undefined,
              description: (systemFields.description as string) || undefined,
              priority: (systemFields.priority as CreateJobRequest['priority']) || undefined,
              assignedPartnerId: (systemFields.assignedPartnerId as string) || undefined,
              assignedManagerId: (systemFields.assignedManagerId as string) || undefined,
              assignedAccountantId: (systemFields.assignedAccountantId as string) || undefined,
              budgetAmount: (systemFields.budgetAmount as string)
                ? parseFloat(systemFields.budgetAmount as string)
                : undefined,
              estimatedHours: (systemFields.estimatedHours as string)
                ? parseFloat(systemFields.estimatedHours as string)
                : undefined,
              startDate: (systemFields.startDate as string) || undefined,
              dueDate: (systemFields.dueDate as string) || undefined,
              formData,
            });
            toast({
              variant: result.failed > 0 ? 'warning' : 'success',
              title: 'Job updated successfully',
              description: summarizeGroupResult(result),
            });
          } catch {
            toast({
              variant: 'warning',
              title: 'Job updated, but group member jobs failed',
              description: 'The job was updated; creating jobs for group members failed.',
            });
          }
        } else {
          toast({ variant: 'success', title: 'Job updated successfully' });
        }
        navigate(`/jobs/${jobId}`);
      } catch (err) {
        toast({
          variant: 'error',
          title: 'Failed to update job',
          description: extractApiErrorMessage(err, 'Please check the form and try again.'),
        });
      }
    } else {
      const request: CreateJobRequest = {
        clientId: systemFields.clientId as string,
        entityTypeId: (entityTypeId as string) || undefined,
        practiceEntityId: (systemFields.practiceEntityId as string) || undefined,
        jobType: resolvedJobType || undefined,
        workflowTemplateId: (systemFields.workflowTemplateId as string) || undefined,
        title: (systemFields.title as string) || undefined,
        description: (systemFields.description as string) || undefined,
        priority: (systemFields.priority as CreateJobRequest['priority']) || undefined,
        assignedPartnerId: (systemFields.assignedPartnerId as string) || undefined,
        assignedManagerId: (systemFields.assignedManagerId as string) || undefined,
        assignedAccountantId: (systemFields.assignedAccountantId as string) || undefined,
        budgetAmount: (systemFields.budgetAmount as string)
          ? parseFloat(systemFields.budgetAmount as string)
          : undefined,
        estimatedHours: (systemFields.estimatedHours as string)
          ? parseFloat(systemFields.estimatedHours as string)
          : undefined,
        startDate: (systemFields.startDate as string) || undefined,
        dueDate: (systemFields.dueDate as string) || undefined,
        isRecurring,
        recurrenceFrequency: isRecurring
          ? (systemFields.recurrenceFrequency as string) || undefined
          : undefined,
        recurrenceNextDate: isRecurring
          ? (systemFields.recurrenceNextDate as string) || undefined
          : undefined,
        formData,
      };

      try {
        await createJob.mutateAsync(request);

        // Fan the same job out to any selected group members (each gets its own
        // individual job). Failures here are reported but don't undo the
        // primary job.
        if (selectedGroupMemberIds.length > 0) {
          const allowDuplicates = resolveAllowDuplicates();
          try {
            const result = await createGroupJobs.mutateAsync({
              clientIds: selectedGroupMemberIds,
              allowDuplicates: allowDuplicates === true,
              entityTypeId: (entityTypeId as string) || undefined,
              practiceEntityId: (systemFields.practiceEntityId as string) || undefined,
              jobType: resolvedJobType || undefined,
              workflowTemplateId: (systemFields.workflowTemplateId as string) || undefined,
              title: (systemFields.title as string) || undefined,
              description: (systemFields.description as string) || undefined,
              priority: (systemFields.priority as CreateJobRequest['priority']) || undefined,
              assignedPartnerId: (systemFields.assignedPartnerId as string) || undefined,
              assignedManagerId: (systemFields.assignedManagerId as string) || undefined,
              assignedAccountantId: (systemFields.assignedAccountantId as string) || undefined,
              budgetAmount: (systemFields.budgetAmount as string)
                ? parseFloat(systemFields.budgetAmount as string)
                : undefined,
              estimatedHours: (systemFields.estimatedHours as string)
                ? parseFloat(systemFields.estimatedHours as string)
                : undefined,
              startDate: (systemFields.startDate as string) || undefined,
              dueDate: (systemFields.dueDate as string) || undefined,
              formData,
            });
            toast({
              variant: result.failed > 0 ? 'warning' : 'success',
              title: 'Job created successfully',
              description: summarizeGroupResult(result),
            });
          } catch {
            toast({
              variant: 'warning',
              title: 'Job created, but group member jobs failed',
              description: 'The primary job was created; creating jobs for group members failed.',
            });
          }
        } else {
          toast({ variant: 'success', title: 'Job created successfully' });
        }
        navigate('/jobs');
      } catch (err) {
        toast({
          variant: 'error',
          title: 'Failed to create job',
          description: extractApiErrorMessage(err, 'Please check the form and try again.'),
        });
      }
    }
  };

  // Build job entity type options for the selector
  const entityTypeOptions = (jobEntityTypes ?? [])
    .filter((et) => et.isActive)
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((et) => ({ value: et.id, label: et.name }));

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {isEditMode && jobLoading ? (
        <div className="flex items-center justify-center py-20">
          <p className="text-sm text-gray-500">Loading job...</p>
        </div>
      ) : isEditMode && !existingJob && !jobLoading ? (
        <div className="py-20 text-center">
          <p className="text-gray-500">Job not found</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/jobs')}>
            Back to Jobs
          </Button>
        </div>
      ) : (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Header with job type inline + actions. Wraps on narrow widths so
                the extra workflow selector never squeezes the title/buttons. */}
            <div className="flex flex-wrap items-center justify-between gap-y-3 border-b border-border pb-4 dark:border-border-dark">
              <div className="flex flex-1 flex-wrap items-center gap-x-3 gap-y-2">
                <h1 className="whitespace-nowrap text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {isEditMode ? 'Edit Job' : 'Create Job'}
                </h1>
                <div className="min-w-[11rem] flex-1 sm:max-w-[16rem]">
                  <FormSelect
                    name="entityTypeId"
                    control={methods.control}
                    label=""
                    options={entityTypeOptions}
                    placeholder="Select job type..."
                    required
                    disabled={isEditMode}
                  />
                </div>
                {/* Workflow Template selector — shown in the header next to the
                    job type ONLY when the form-builder template doesn't already
                    position a workflow field, and the selected job type has at
                    least one matching active template. Bound to the same
                    `workflowTemplateId` value the auto-select effect fills. */}
                {selectedEntityTypeId &&
                  !formHasWorkflowField &&
                  workflowTemplateOptions.length > 0 && (
                    <div className="min-w-[11rem] flex-1 sm:max-w-[16rem]">
                      <FormSelect
                        name="workflowTemplateId"
                        control={methods.control}
                        label=""
                        options={workflowTemplateOptions}
                        placeholder="Select workflow..."
                        disabled={isEditMode && !canChangeWorkflow}
                      />
                    </div>
                  )}
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(isEditMode ? `/jobs/${jobId}` : '/jobs')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={isEditMode ? updateJob.isPending : createJob.isPending}
                >
                  {isEditMode ? 'Save Changes' : 'Create Job'}
                </Button>
              </div>
            </div>

            {/* Dynamic Form Content with Tabs */}
            {selectedEntityTypeId && (
              <JobFormTabsContent
                formDefinition={formDefinition ?? undefined}
                formLoading={formLoading}
                disabledCategories={disabledCategories}
                createdOn={toDateInput(existingJob?.createdAt) || todayYmd()}
                showGroupMembers
                selectedGroupMemberIds={selectedGroupMemberIds}
                onGroupMembersChange={setSelectedGroupMemberIds}
                onDuplicateMembersChange={setDuplicateGroupMemberIds}
                jobEntityTypes={jobEntityTypes ?? []}
              />
            )}
          </form>
        </FormProvider>
      )}
    </div>
  );
}

/**
 * Reads the currently selected client from form context and renders the group
 * member fan-out picker. Lives inside FormProvider so it can watch clientId.
 */
function GroupMemberFanoutSection({
  selectedIds,
  onChange,
  onDuplicatesChange,
  jobEntityTypes,
}: {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  onDuplicatesChange: (ids: string[]) => void;
  jobEntityTypes: Array<{ id: string; name: string }>;
}) {
  const { watch } = useFormContext();
  const clientId = (watch('clientId') as string) || '';
  const entityTypeId = (watch('entityTypeId') as string) || '';
  const jobTypeField = (watch('jobType') as string) || '';
  // Resolve the job type name the same way onSubmit does: explicit jobType
  // field, else the selected entity type's name.
  const resolvedJobType =
    jobTypeField || jobEntityTypes.find((et) => et.id === entityTypeId)?.name || '';

  if (!clientId) {
    return (
      <Card>
        <CardContent>
          <p className="py-4 text-sm text-gray-500 dark:text-gray-400">
            Select a client first. If that client belongs to a group, its members will appear here
            so you can create this job for them too.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <GroupMemberJobFanout
      clientId={clientId}
      selectedIds={selectedIds}
      onChange={onChange}
      onDuplicatesChange={onDuplicatesChange}
      jobType={resolvedJobType}
    />
  );
}

// ─── Client Search Field ──────────────────────────────────────────────────────
// Kept from original for the DynamicFormRenderer's 'contacts' lookup_category
// to leverage the existing LookupSelect pattern in DynamicFormRenderer.

// ─── Staff Users Hook (used by lookup selects) ────────────────────────────────
// The DynamicFormRenderer uses lookup_category to resolve users via LookupSelect.
// No separate hook needed here — form rendering is handled entirely by
// the DynamicFormRenderer using the lookup_category definitions from the form fields.

// ─── Job Form Tabs Content Component ──────────────────────────────────────────

function stripRecurrenceFields(
  sections: import('@/features/form-builder/hooks/useFormBuilder').FormSection[],
) {
  return sections.map((section) => ({
    ...section,
    fields: section.fields.filter(
      (field) =>
        !RECURRENCE_FIELD_KEYS.has(field.field_key) &&
        !RECURRENCE_FIELD_KEYS.has(field.db_column ?? ''),
    ),
  }));
}

function JobFormTabsContent({
  formDefinition,
  formLoading,
  disabledCategories,
  createdOn,
  showGroupMembers = false,
  selectedGroupMemberIds = [],
  onGroupMembersChange,
  onDuplicateMembersChange,
  jobEntityTypes = [],
}: {
  formDefinition?: import('@/features/form-builder/hooks/useFormBuilder').FormDefinitionFull;
  formLoading?: boolean;
  disabledCategories?: string[];
  createdOn: string;
  /** Show the "Group Members" tab. */
  showGroupMembers?: boolean;
  selectedGroupMemberIds?: string[];
  onGroupMembersChange?: (ids: string[]) => void;
  onDuplicateMembersChange?: (ids: string[]) => void;
  jobEntityTypes?: Array<{ id: string; name: string }>;
}) {
  const [activeTab, setActiveTab] = useState('');

  const allSections = stripRecurrenceFields(formDefinition?.sections ?? []);
  const formTabSections = allSections.filter((s) => s.render_as === 'tab' && !s.parent_tab_id);
  const orphanSections = allSections.filter(
    (s) => s.render_as !== 'tab' && !s.parent_tab_id && s.is_active,
  );
  const hasOrphans = orphanSections.length > 0;

  const formTabs = [
    ...(formTabSections.length > 0
      ? formTabSections.map((s) => ({ id: `section-${s.id}`, label: s.name }))
      : [{ id: 'form-details', label: 'Details' }]),
    { id: 'recurrence', label: 'Recurrence' },
    ...(showGroupMembers ? [{ id: 'group-members', label: 'Group Members' }] : []),
  ];

  useEffect(() => {
    if (formTabs.length > 0) {
      setActiveTab(formTabs[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formDefinition]);

  return (
    <div className="space-y-6">
      <Tabs tabs={formTabs} activeTab={activeTab} onChange={setActiveTab} />

      {formTabSections.map((tab, tabIndex) => {
        const explicitChildren = allSections
          .filter((s) => s.parent_tab_id === tab.id && s.render_as !== 'tab' && s.is_active)
          .sort((a, b) => a.display_order - b.display_order);

        let implicitChildren: typeof allSections = [];
        if (explicitChildren.length === 0) {
          const nextTab = formTabSections[tabIndex + 1];
          implicitChildren = orphanSections.filter((s) => {
            const afterThisTab = s.display_order >= tab.display_order;
            const beforeNextTab = nextTab ? s.display_order < nextTab.display_order : true;
            return afterThisTab && beforeNextTab;
          });
        }

        const children = explicitChildren.length > 0 ? explicitChildren : implicitChildren;
        const tabHasFields = tab.fields.filter((f) => f.is_active).length > 0;
        const sectionsToRender = [
          ...(tabHasFields ? [{ ...tab, render_as: 'section' as const }] : []),
          ...children,
        ];

        return (
          <TabPanel key={tab.id} tabId={`section-${tab.id}`} activeTab={activeTab}>
            {formDefinition && sectionsToRender.length > 0 ? (
              <DynamicFormRenderer
                formDefinition={{ ...formDefinition, sections: sectionsToRender }}
                disabledCategories={disabledCategories}
              />
            ) : (
              <Card>
                <CardContent>
                  <p className="py-4 text-sm text-gray-500">No fields configured for this tab.</p>
                </CardContent>
              </Card>
            )}
          </TabPanel>
        );
      })}

      {formTabSections.length === 0 && (
        <TabPanel tabId="form-details" activeTab={activeTab || 'form-details'}>
          {formLoading ? (
            <Card>
              <CardContent>
                <div className="flex items-center justify-center py-10">
                  <p className="text-sm text-gray-500">Loading form...</p>
                </div>
              </CardContent>
            </Card>
          ) : formDefinition && hasOrphans ? (
            <DynamicFormRenderer
              formDefinition={{ ...formDefinition, sections: orphanSections }}
              disabledCategories={disabledCategories}
            />
          ) : (
            <Card>
              <CardContent>
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <svg
                    className="h-12 w-12 text-amber-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                    />
                  </svg>
                  <p className="mt-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                    No form attached to this job type
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Please configure a form in Settings → Form Builder and map it to this job type.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabPanel>
      )}

      <TabPanel tabId="recurrence" activeTab={activeTab}>
        <RecurrenceSection createdOn={createdOn} />
      </TabPanel>

      {showGroupMembers && (
        <TabPanel tabId="group-members" activeTab={activeTab}>
          <GroupMemberFanoutSection
            selectedIds={selectedGroupMemberIds}
            onChange={onGroupMembersChange ?? (() => {})}
            onDuplicatesChange={onDuplicateMembersChange ?? (() => {})}
            jobEntityTypes={jobEntityTypes}
          />
        </TabPanel>
      )}
    </div>
  );
}

function RecurrenceSection({ createdOn }: { createdOn: string }) {
  const { control, watch, setValue, getValues } = useFormContext();
  const isRecurring = Boolean(watch('isRecurring'));
  const frequency = watch('recurrenceFrequency') as string;
  const clientId = watch('clientId') as string;
  const { data: client } = useContact(isRecurring && frequency === 'quarterly' ? clientId : '');
  const { data: generalSettings } = useQuery({
    queryKey: ['settings', 'general'],
    queryFn: async () => {
      const response =
        await apiClient.get<ApiResponse<{ fiscalYearStart?: string }>>('/settings/general');
      return response.data.data;
    },
    enabled: isRecurring,
  });
  const lastFrequency = useRef<string | undefined>(undefined);
  const lastClientId = useRef<string | undefined>(undefined);
  const today = todayYmd();

  useEffect(() => {
    if (!isRecurring) return;
    if (frequency !== 'monthly' && frequency !== 'quarterly' && frequency !== 'annually') return;

    const frequencyChanged =
      lastFrequency.current !== undefined && lastFrequency.current !== frequency;
    const clientChanged = lastClientId.current !== undefined && lastClientId.current !== clientId;
    lastFrequency.current = frequency;
    lastClientId.current = clientId;

    const existingNext = String(getValues('recurrenceNextDate') ?? '').slice(0, 10);
    const shouldSuggest = frequencyChanged || clientChanged || !existingNext;
    if (!shouldSuggest) return;

    if (frequency === 'quarterly' && clientId && !client) return;

    const fiscalYearStart =
      extractFinancialStart({
        formFieldValues: client?.formFieldValues,
        formData: (client as { formData?: Record<string, unknown> | string | null } | undefined)
          ?.formData,
        dynamicFields: client?.dynamicFields,
      }) ??
      generalSettings?.fiscalYearStart ??
      null;
    if (frequency === 'quarterly' && !fiscalYearStart) return;

    const suggested = suggestRecurrenceNextDate({
      frequency: frequency as JobRecurrenceFrequency,
      createdOn: createdOn || today,
      fiscalYearStart,
      today,
    });
    if (!suggested) return;
    setValue('recurrenceNextDate', suggested);
  }, [
    isRecurring,
    frequency,
    client,
    clientId,
    generalSettings?.fiscalYearStart,
    createdOn,
    getValues,
    setValue,
    today,
  ]);

  return (
    <Card>
      <CardHeader title="Recurrence" description="A new job is created on the next date" />
      <CardContent>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <FormCheckbox
              name="isRecurring"
              control={control}
              label="Recurring Job"
              description="Create a new job automatically on the next date. Off by default."
            />
          </div>
          {isRecurring && (
            <>
              <FormSelect
                name="recurrenceFrequency"
                control={control}
                label="Frequency"
                required
                options={FREQUENCY_OPTIONS}
                placeholder="Select frequency..."
              />
              <FormInput
                name="recurrenceNextDate"
                control={control}
                label="Next Date"
                type="date"
                required
                min={tomorrowYmd()}
              />
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
